/**
 * WhatsApp Broadcast Template
 *
 * Canal de difusión de WhatsApp — suscripción mensual.
 * Sin email input: el botón CTA genera un checkout en MercadoPago directamente.
 * Tras el pago → /registro?from_basic=1&plan_tier=whatsapp_broadcast&from_mp=true
 * donde el usuario crea su cuenta y espera el link de acceso al canal.
 *
 * Env vars:
 *   VITE_WHATSAPP_BROADCAST_PRICE_ARS    (precio ARS, default 6999)
 *   VITE_WHATSAPP_BROADCAST_HERO_TITLE   (default "canal de WhatsApp")
 *   VITE_WHATSAPP_BROADCAST_HERO_TAGLINE (default auto)
 *   VITE_WHATSAPP_BROADCAST_INTRO        (default genérico)
 *   VITE_WHATSAPP_BROADCAST_QUOTE        (cita personal, default genérico)
 *   VITE_WHATSAPP_BROADCAST_BG_IMAGE     (fondo opcional)
 */

import { BRAND_PERSON_NAME, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { WHATSAPP_BROADCAST_CONFIG } from "@/config/whatsappBroadcastConfig";
import { CheckCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const ENV_PRICE_ARS = WHATSAPP_BROADCAST_CONFIG.priceARS || 6999;

const HERO_TITLE =
  (import.meta.env.VITE_WHATSAPP_BROADCAST_HERO_TITLE as string | undefined)?.trim() ||
  "canal de WhatsApp";
const HERO_TAGLINE =
  (import.meta.env.VITE_WHATSAPP_BROADCAST_HERO_TAGLINE as string | undefined)?.trim() ||
  "Suscripción mensual · Solo Argentina";
const INTRO_TEXT =
  (import.meta.env.VITE_WHATSAPP_BROADCAST_INTRO as string | undefined)?.trim() ||
  "Es mi canal personal donde comparto mensajes, fotos y momentos de mi día a día que no subo a ningún otro lado.";
const QUOTE_TEXT =
  (import.meta.env.VITE_WHATSAPP_BROADCAST_QUOTE as string | undefined)?.trim() ||
  "Quería un lugar donde estar de verdad con la gente que me sigue. Sin likes, sin algoritmo — solo escribir y que llegue directo.";

const SUCCESS_PATH = "/registro?from_basic=1&plan_tier=whatsapp_broadcast&from_mp=true";
const FAILURE_PATH = "/whatsapp-broadcast";
const PENDING_PATH = "/registro?from_basic=1&plan_tier=whatsapp_broadcast&from_mp=true";

const WHATSAPP_SVG = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="w-full h-full">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const BENEFITS = [
  { emoji: "📲", text: "Mensajes de mi día a día — lo que no subo a ningún otro lado" },
  { emoji: "💚", text: "Grupo chico, tranquilo y con muy buena onda" },
  { emoji: "🙋‍♀️", text: "Soy yo quien escribe, no un equipo ni una agencia" },
  { emoji: "🌿", text: "Sin spam, sin publicidad — solo cosas que me importan de verdad" },
];

const INCLUDES = [
  "Acceso al canal privado de WhatsApp",
  "Recibís mensajes directos de mí — mi día a día real",
  "Parte de un grupo pequeño y positivo",
  "Podés cancelar cuando querás, sin preguntas",
];

export default function WhatsAppBroadcastTemplate() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [priceARS, setPriceARS] = useState(ENV_PRICE_ARS);
  const [planName, setPlanName] = useState(`Canal de WhatsApp — ${BRAND_PERSON_NAME}`);
  const [pageEnabled, setPageEnabled] = useState(true);

  // Fetch live config from admin API (falls back to env var if unavailable)
  useEffect(() => {
    fetch("/api/admin/whatsapp-broadcast-config")
      .then((r) => r.ok ? r.json() : null)
      .then((data: { config?: { price_ars: number; plan_name: string; enabled: boolean } } | null) => {
        if (data?.config) {
          if (data.config.price_ars > 0) setPriceARS(data.config.price_ars);
          if (data.config.plan_name) setPlanName(`${data.config.plan_name} — ${BRAND_PERSON_NAME}`);
          setPageEnabled(data.config.enabled !== false);
        }
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  const bgUrl = WHATSAPP_BROADCAST_CONFIG.backgroundImageUrl;

  const handleSubscribe = async () => {
    setError(null);
    setIsLoading(true);
    try {
      // create-subscription con auto_recurring (sin preapproval_plan_id) + skipPayerEmail:true
      // MP genera una suscripción mensual recurrente y pide datos del pagador en su propio checkout
      const res = await fetch("/api/mercadopago/create-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason: planName,
          productName: planName,
          price: priceARS,       // ARS en unidades mayores (ej. 6999)
          currency: "ARS",
          country: "AR",
          skipPayerEmail: true,  // MP recolecta el email en su propio formulario
          successPath: SUCCESS_PATH,
          failurePath: FAILURE_PATH,
          pendingPath: PENDING_PATH,
          metadata: {
            source: BRAND_SOURCE_KEY,
            type: "whatsapp_broadcast_subscription",
            flow: "whatsapp_broadcast",
            plan_tier: "whatsapp_broadcast",
            price_ars: priceARS,
          },
        }),
      });

      const json = (await res.json().catch(() => ({}))) as {
        init_point?: string;
        error?: string;
        code?: string;
      };

      if (!res.ok || !json.init_point) {
        throw new Error(json.error ?? "No se pudo iniciar el pago. Intentá de nuevo.");
      }

      window.location.assign(json.init_point);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error de conexión. Intentá de nuevo.");
      setIsLoading(false);
    }
  };

  // Disable CTA if page is marked inactive in admin
  const ctaDisabled = isLoading || !pageEnabled;

  const bgStyle = bgUrl
    ? { backgroundImage: `url(${bgUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { background: "linear-gradient(160deg, #0a1a0f 0%, #0d2b1a 50%, #091a10 100%)" };

  const formattedPrice = new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(priceARS);

  return (
    <div className="min-h-screen w-full antialiased" style={bgStyle}>
      {bgUrl && <div className="fixed inset-0 bg-black/55 pointer-events-none" />}

      <div className="relative min-h-screen flex flex-col">

        {/* Header */}
        <header className="sticky top-0 z-10 bg-black/60 backdrop-blur-md border-b border-white/10">
          <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
            <button
              onClick={() => navigate("/")}
              aria-label="Volver al inicio"
              className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Volver
            </button>

            <div className="flex items-center gap-2">
              <span className="inline-flex w-5 h-5 items-center justify-center rounded-md bg-[#25D366] text-white p-0.5">
                <span className="block w-full h-full">{WHATSAPP_SVG}</span>
              </span>
              <span className="text-sm font-semibold text-white">Canal de WhatsApp</span>
            </div>

            <div className="w-14" />
          </div>
        </header>

        {/* Main */}
        <main className="flex-1 max-w-lg mx-auto w-full px-4 py-8 space-y-8">

          {/* Hero */}
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="w-20 h-20 rounded-2xl bg-[#25D366] flex items-center justify-center shadow-lg shadow-green-500/30 p-4">
                <span className="text-white">{WHATSAPP_SVG}</span>
              </div>
            </div>

            <div className="space-y-3">
              <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight tracking-tight">
                Sumate a mi<br />
                <span className="text-[#25D366]">{HERO_TITLE}</span>
              </h1>
              <p className="text-white/50 text-sm font-medium">{HERO_TAGLINE}</p>
            </div>

            <p className="text-white/75 text-base sm:text-lg leading-relaxed max-w-sm mx-auto">
              {INTRO_TEXT}{" "}
              <strong className="text-white font-semibold">Sin algoritmo, sin público.</strong>{" "}
              Solo vos y mi círculo más cercano.
            </p>
          </div>

          {/* Beneficios */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-5 space-y-4">
            <p className="text-xs font-semibold text-white/40 uppercase tracking-wider">
              ¿Qué vas a recibir?
            </p>
            {BENEFITS.map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="text-xl leading-none mt-0.5 flex-shrink-0">{b.emoji}</span>
                <span className="text-white/80 text-sm sm:text-base leading-snug">{b.text}</span>
              </div>
            ))}
          </div>

          {/* Quote */}
          <blockquote className="bg-white/5 backdrop-blur-sm border-l-4 border-[#25D366] rounded-r-2xl px-5 py-4">
            <p className="text-white/70 text-sm sm:text-base italic leading-relaxed">
              &quot;{QUOTE_TEXT}&quot;
            </p>
            <footer className="mt-2 text-[#25D366] font-semibold text-sm">
              — {BRAND_PERSON_NAME}
            </footer>
          </blockquote>

          {/* Card suscripción */}
          <div className="bg-zinc-900/80 backdrop-blur-sm rounded-3xl border border-white/10 overflow-hidden shadow-xl shadow-black/40">

            {/* Banner precio */}
            <div className="bg-[#25D366] px-6 py-5 text-center">
              <p className="text-white/90 text-sm font-semibold mb-1">
                Suscripción mensual
              </p>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-5xl font-bold text-white">{formattedPrice}</span>
                <span className="text-white/80 text-sm">/ mes</span>
              </div>
              <p className="text-white/70 text-xs mt-1.5">
                Cancelás cuando querés — sin preguntas, sin drama
              </p>
            </div>

            <div className="p-6 space-y-5">

              {/* Incluye */}
              <div className="space-y-2.5">
                {INCLUDES.map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <CheckCircle className="w-4 h-4 text-[#25D366] flex-shrink-0 mt-0.5" />
                    <span className="text-white/80 text-sm leading-snug">{item}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10" />

              {/* Error */}
              {error && (
                <div role="alert" className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
                  <p className="text-red-400 text-sm font-medium">{error}</p>
                </div>
              )}

              {/* CTA */}
              <button
                onClick={handleSubscribe}
                disabled={ctaDisabled}
                className="w-full bg-[#25D366] hover:bg-[#20bd5a] active:scale-[0.98] text-white font-bold text-base py-4 px-6 rounded-2xl ui-transition flex items-center justify-center gap-2.5 shadow-lg shadow-green-500/20 disabled:opacity-50 disabled:cursor-not-allowed min-h-[56px]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Preparando pago…
                  </>
                ) : (
                  <>
                    <span className="inline-flex w-5 h-5 shrink-0">{WHATSAPP_SVG}</span>
                    ¡Quiero unirme al canal!
                  </>
                )}
              </button>

              <p className="text-xs text-white/40 text-center leading-relaxed">
                Vas a ir a Mercado Pago para pagar de forma segura. Después te enviamos el link de acceso al canal.
              </p>
            </div>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { icon: "🔒", label: "Pago seguro por Mercado Pago" },
              { icon: "🔄", label: "Cancelás cuando querés" },
              { icon: "⚡", label: "Link de acceso el mismo día" },
            ].map((b, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 text-white/60 text-xs font-medium px-3 py-1.5 rounded-full"
              >
                <span>{b.icon}</span>
                {b.label}
              </span>
            ))}
          </div>
        </main>

        {/* Footer */}
        <footer className="max-w-lg mx-auto w-full px-4 pb-8 pt-4 text-center space-y-2">
          <button
            onClick={() => navigate("/")}
            className="text-xs text-white/30 hover:text-white/60 transition-colors"
          >
            ← Volver al inicio
          </button>
        </footer>
      </div>
    </div>
  );
}
